import { useRef, useState } from 'react';
import { HeaderProps } from './Header';
import LogoutIcon from '../../../assets/logout.svg';
import { useAppContext } from '../../../AppContext';
import { Page } from '../../../models/Page';
import { datadogLogs } from '@datadog/browser-logs';
import { REPORT_IMPROVEMENT_FORM_URL } from '../../constants';
import { useAgentsDashboardAccess } from './useAgentsDashboardAccess';
import { ProfileMenuSkeleton } from './ProfileMenuSkeleton';
import { Avatar, AvatarProps, Typography, Box, Divider, Menu, MenuItem } from '@coolblue-development/freddy';
import { Subsidiary, useSubsidiary } from '../../../utils/hooks/useSubsidiary';

// Temporary rollout gate, one country per day (de, then nl, then be). Agents on the Customer Service Labs
// routing profile keep the Old Panel to finish contacts Cato can't handle yet. Remove with the Old Panel (CSD-3483)
const OLD_PANEL_DISABLED_SUBSIDIARIES: Subsidiary[] = ['be'];

interface ProfileMenuProps extends HeaderProps {
  avatarSize?: AvatarProps['size'];
}

const ProfileMenu: React.FC<ProfileMenuProps> = ({ instanceUrl, avatarSize }) => {
  const { setActivePage, agent } = useAppContext();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const agentName = agent?.getName();
  const { isCheckingAccess, isAuthorized, checkAccess } = useAgentsDashboardAccess();
  const subsidiary = useSubsidiary();
  const isCustomerServiceLabs =
    agent?.getRoutingProfile().name.toLowerCase().includes('customer service labs') ?? false;
  const showOldPanel = isCustomerServiceLabs || !OLD_PANEL_DISABLED_SUBSIDIARIES.includes(subsidiary);

  const handleClick = async (): Promise<void> => {
    const nextOpen = !open;
    setOpen(nextOpen);

    if (nextOpen && agent) {
      const agentArn = agent.getAgentARN();
      await checkAccess(agentArn);
    }
  };

  const handleClose = (): void => {
    setOpen(false);
  };

  const handleCCPPage = (): void => {
    setActivePage(Page.CCP);
    handleClose();

    datadogLogs.logger.info(`CCP panel opened`, { ccpPanel: true });
  };

  const handleSettingsPage = (): void => {
    setActivePage(Page.SETTINGS);
    handleClose();
  };

  const handleAgentsDashboardPage = (): void => {
    setActivePage(Page.AGENTS_DASHBOARD);
    handleClose();
  };

  const handleLogout = (): void => {
    const logoutUrl = `${instanceUrl}/connect/logout`;
    window.location.replace(logoutUrl);
  };

  return (
    <>
      <Avatar
        ref={buttonRef}
        component="button"
        size={avatarSize}
        onClick={handleClick}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
      >
        {agentName?.charAt(0)}
      </Avatar>

      <Menu
        referenceElement={buttonRef.current}
        open={open}
        onClose={handleClose}
        placement="bottom-end"
        style={{ zIndex: 10000 }}
      >
        <MenuItem onClick={handleClose}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar size="large">{agentName?.charAt(0)}</Avatar>
            <Box>
              <Typography variant="h4">{agentName}</Typography>
              <Typography>Customer Service</Typography>
            </Box>
          </Box>
        </MenuItem>

        <Divider />

        {isCheckingAccess ? (
          <ProfileMenuSkeleton />
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              padding: 1,
              width: '100%'
            }}
          >
            <MenuItem onClick={() => window.open(REPORT_IMPROVEMENT_FORM_URL, '_blank')}>
              <Typography>Report Improvement</Typography>
            </MenuItem>

            {showOldPanel && (
              <MenuItem onClick={handleCCPPage}>
                <Typography>Old Panel</Typography>
              </MenuItem>
            )}

            <MenuItem onClick={handleSettingsPage}>
              <Typography>Settings</Typography>
            </MenuItem>

            {isAuthorized && (
              <MenuItem onClick={handleAgentsDashboardPage}>
                <Typography>{Page.AGENTS_DASHBOARD}</Typography>
              </MenuItem>
            )}

            <MenuItem onClick={handleLogout}>
              <LogoutIcon />
              <Typography sx={{ marginLeft: '4px' }}>Logout</Typography>
            </MenuItem>
          </Box>
        )}
      </Menu>
    </>
  );
};

export default ProfileMenu;
